import type { UnreadNotificationsCount } from '@repo/types/notifications';

import { isEmptyArray } from '@repo/lib/assertion';
import { NotificationType } from '@repo/types/enums/notificationType';
import { useQuery } from '@tanstack/react-query';

import { NotificationKey } from './keys';
import { NotificationService } from './notificationService';

/**
 * Calculates the total count across all notification types.
 *
 * @param data - Array of unread notification counts by type
 * @returns The sum of all notification counts
 */
function calculateTotalCount(data: UnreadNotificationsCount[]): number {
  if (isEmptyArray(data)) {
    return 0;
  }

  return data.map((item) => parseInt(item.count, 10)).reduce((acc, curr) => acc + curr, 0);
}

/**
 * Finds and returns the count for a specific notification type.
 *
 * @param data - Array of unread notification counts by type
 * @param type - The notification type to find
 * @returns The count for the specified type, or 0 if not found
 */
function getCountByType(data: UnreadNotificationsCount[], type: NotificationType): number {
  const result = data.find((item) => item.type === type);
  return result ? parseInt(result.count) : 0;
}

/**
 * Returns all valid notification types (excluding 'all').
 *
 * @returns Array of notification types
 */
function getNotificationTypes(): NotificationType[] {
  return Object.values(NotificationType).filter((type) => type !== NotificationType.All);
}

/**
 * Creates the initial data structure with all notification types set to zero.
 *
 * @returns Array of unread notification counts initialized to '0'
 */
function createInitialData(): UnreadNotificationsCount[] {
  return getNotificationTypes().map((type) => ({ type, count: '0' }));
}

/**
 * Normalizes the API response to ensure all notification types are represented.
 *
 * @param data - The raw API response with notification counts
 * @returns Normalized array with all notification types included
 */
function normalizeNotificationCounts(data: UnreadNotificationsCount[]): UnreadNotificationsCount[] {
  const fetchedCountsByType = data.reduce(
    (acc, item) => {
      acc[item.type] = item.count;
      return acc;
    },
    {} as Record<string, string>
  );

  return getNotificationTypes().map((type) => ({
    type,
    count: fetchedCountsByType[type] || '0',
  }));
}

/**
 * Selects and transforms the unread notification count based on the requested type.
 *
 * @param data - Array of unread notification counts by type
 * @param type - The notification type to select (or undefined)
 * @returns The count for the specified type, total count if 'all', or 0
 */
function selectNotificationCount(data: UnreadNotificationsCount[], type?: NotificationType): number {
  if (!type) {
    return 0;
  }

  if (type === NotificationType.All) {
    return calculateTotalCount(data);
  }

  return getCountByType(data, type);
}

/**
 * Hook to fetch and select unread notification counts.
 *
 * This hook fetches the count of unread notifications from the server
 * and provides a selector to get counts by type or total.
 *
 * @param type - The notification type to filter by. Use 'all' for total count.
 * @param enabled - Whether the query should be enabled. Defaults to true.
 * @returns React Query result with the selected count as data
 *
 * @example
 * ```tsx
 * // Get count for a specific type
 * const { data: chatCount } = useGetUnreadNotificationsCount(NotificationType.Chat);
 *
 * // Get total count across all types
 * const { data: totalCount } = useGetUnreadNotificationsCount(NotificationType.All);
 *
 * // Conditionally enable the query
 * const { data } = useGetUnreadNotificationsCount(NotificationType.Chat, isLoggedIn);
 * ```
 */
export function useGetUnreadNotificationsCount(type?: NotificationType, enabled = true) {
  return useQuery({
    queryKey: [NotificationKey.UNREAD_NOTIFICATIONS_COUNT],
    enabled: enabled && !!type,
    select: (data: UnreadNotificationsCount[]) => selectNotificationCount(data, type),
    initialData: createInitialData,
    queryFn: async () => {
      const response = await NotificationService.getUnreadNotificationsCount();
      return normalizeNotificationCounts(response.data);
    },
  });
}
